"use client";

/**
 * 常用功能面板 —— 首页**唯一**的模块入口（2026-10-08 二次改版）。
 *
 * 🔴 为什么只剩一个面板：
 * 首版是「快捷入口（全量宫格）+ 常用功能（置顶/排序）」双面板，但它们读的是
 * **同一张注册表**（`HOME_MODULES`）的同一批对象 —— 用户截图反馈「两个板块重复了」。
 * 重复入口不是便利而是噪音：同样的 9 张卡在一屏里出现两遍，页面高度翻倍。
 * 现在只保留「常用功能」：支持置顶/排序（快捷入口没有的能力），整卡可点直达。
 *
 * 整卡可点的实现是 **stretched link** 模式：标题 Link 用 `::after` 铺满整卡，
 * 卡内其它元素（置顶按钮）用 `z-10` 浮在链接层之上 —— 点击不再穿透误触跳转。
 *
 * ## 🔴 卡片形态：单行横条，不是竖排小卡（2026-10-08 第 6 轮）
 *
 * 用户截图指出的根因不是「间距大」，而是**单位面积的信息量太低**：
 * 竖排卡把「图标 / 标题 / 说明 / 访问次数」摊成 3 行 101px 高，
 * 一屏只能放 10 张，却只承载 4 个短语 —— 整块在页面里占掉约 210px 却不给信息。
 * 改成单行横条后同样的 4 个短语只占 36px：
 *   `[图标] 标题  说明(截断)  访问N次`
 * 5 列 × 2 行从 210px 降到 80px，且说明文字**完整放得下**（不再被截成 6 个字）。
 *
 * 🔴 等高由**结构**保证，不靠占位 hack：
 * 图标是固定 `h-6 w-6`，无论说明/访问次数有没有内容，卡片都是同一高度。
 * （上一轮用「空 <p> + h-4」硬撑行高，是因为那时图标与文字是纵向堆叠的；
 *  横条之后行数恒为 1，那个 hack 自然失效 —— 换形态就要换等高机制。）
 *
 * ## 🔴 面板位置：紧跟页头（2026-10-08 第 6 轮）
 *
 * 首版把它放在页面末尾，理由是「读完看板再找入口」。用户截图否决了这个动线：
 * 门户首页的模块入口是**进门第一件要做的事**，压在 4 个图表后面等于埋起来。
 * 现在顺序是「页头 → 入口 → 指标 → 明细」，入口是导航，其余是数据。
 *
 * ## 🔴 href 约定（2026-10-08 用户截图实锤的双前缀 bug）：
 * 模块卡是 `next/link`，**框架会自动补 basePath**（见 `lib/base-path.ts` 顶部说明）。
 * 首版把 `resolveModuleHref(m, locale, basePath)` 的成品（已含前缀）再交给 Link，
 * 浏览器里 href 变成 `/plugins/.../plugins/.../zh/...` 双前缀 —— 不报错、
 * 只有悬停状态栏和点击 404 能暴露。凡走 Link / router.push 的路径一律
 * **不带** basePath（与 global-header 的 `${base}` 同约定）；手工补前缀只允许
 * 出现在原生 `<a href>` 上（框架不碰原生锚点）。
 */

import { useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  ArrowDownWideNarrow,
  BookOpen,
  Bot,
  EyeOff,
  FolderOpen,
  Images,
  Lightbulb,
  Palette,
  Pin,
  PinOff,
  Plus,
  Settings,
  Wand2,
  Workflow,
  type LucideIcon,
} from "lucide-react";

import { F } from "@/components/studio/ui";
import { HOME_MODULES, resolveModuleHref } from "@/lib/home/modules";
import { useSortedModules, type VisitSort } from "@/lib/home/use-home-prefs";
import type { HomeModule, HomeModuleId } from "@/lib/home/types";

import { HomeChip, HomeIconBtn, HomeSection } from "./home-primitives";

/**
 * 图标名 → 组件。
 *
 * 🔴 注册表（`modules.ts`）保持**纯数据**（存图标名字符串），
 * 这样它能被 Node 脚本直接 import 做门禁断言（门禁要验证 href 全组合，
 * 若注册表 import 了 tsx 组件就没法在纯 Node 里跑）。
 * 代价是这张映射表必须**覆盖注册表里所有图标名** ——
 * 漏一个会渲染成空白图标且不报错，所以由门禁 `home-dashboard-gate.test.mjs`
 * 双向断言（注册表 ⊆ 本表，且本表 ⊆ 注册表）。
 */
export const MODULE_ICONS: Record<string, LucideIcon> = {
  Bot,
  FolderOpen,
  Palette,
  Workflow,
  Images,
  BookOpen,
  Wand2,
  Settings,
};

export function ModuleIcon({ name, className = "h-4 w-4" }: { name: string; className?: string }) {
  const Icon = MODULE_ICONS[name] ?? Lightbulb;
  return <Icon className={className} aria-hidden />;
}

// ── 模块卡 ──────────────────────────────────────────────────────

function ModuleCard({
  module,
  href,
  usage,
  pinned,
  onTogglePin,
  onMove,
  onHide,
}: {
  module: HomeModule;
  href: string;
  usage?: { count: number; lastAt: string };
  pinned?: boolean;
  onTogglePin?: (id: HomeModuleId) => void;
  onMove?: (id: HomeModuleId, dir: -1 | 1) => void;
  /**
   * 从常用功能里移除。
   *
   * 🔴 移除按钮必须**只在这里**（悬停工具条内），不给卡片加右键菜单：
   *    右键菜单在触屏/触控板上不可发现，而「移除」是不可后悔动作的最小化版本 ——
   *    （可后悔的部分由「添加」区里的「加入」兜住，所以不需要二次确认弹窗）。
   */
  onHide?: (id: HomeModuleId) => void;
}) {
  const t = useTranslations("home");
  // 🔴 根翻译器（无参）：module.labelKey / descKey 带 `home.` 前缀（注册表里写死的根路径），
  // 用命名空间翻译器会变成 `home.home.modules.x` 并把键名印到界面。
  const tRoot = useTranslations();

  return (
    // 🔴 单行横条：图标 / 标题 / 说明 / 访问次数 挤在同一行（见文件头）。
    // 图标固定 h-6 w-6 → 行数恒为 1 → 无论说明与次数有无内容，卡片都等高，
    // 不需要「空 <p> + 显式高度」那种占位 hack。
    <div
      className="group relative flex min-w-0 items-center gap-2 rounded-lg border border-[var(--border-subtle)] bg-white px-2 py-1 transition-colors hover:border-[var(--primary)]/35 hover:shadow-sm"
      data-module={module.id}
    >
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--primary)]/10 text-[var(--primary)]">
        <ModuleIcon name={module.icon} className="h-3.5 w-3.5" />
      </span>
      {/* 整卡可点（stretched link）：::after 铺满整卡，点卡片任意空白处都跳转。
          置顶/排序按钮容器用 z-10 浮在链接层上，点按钮不会穿透触发跳转。
          🔴 `after:content-['']` 不能省 —— 没有内容的伪元素默认不渲染，
          漏掉它整卡可点就静默失效（与 animate-[关键帧不存在] 同一类静默坑）。 */}
      <Link
        href={href}
        className={`shrink-0 text-[13px] font-medium text-[var(--text-primary)] transition-colors after:absolute after:inset-0 after:content-[''] group-hover:text-[var(--primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]`}
      >
        {tRoot(module.labelKey)}
      </Link>

      {/* 说明单行截断。横条形态下行数恒为 1，所以截断**不再**造成高度参差
          （上一轮竖排形态下 line-clamp-2 会让 9 张卡裂成两档，这是同一个病的不同表现）。
          auto-fit 列宽保证它在 ≥260px 的格子里放得下；仍放不下时 title 属性兜底全文。 */}
      <span className={`${F.deco} min-w-0 flex-1 truncate`} title={tRoot(module.descKey)}>
        {tRoot(module.descKey)}
      </span>

      {/* 访问次数。恒渲染（无记录时为空）—— 横条形态下高度由图标锁死，
          空与不空只影响右侧留白，不影响卡片高度；恒渲染则保证「有/无记录」
          的卡在同一行里右端对齐一致。 */}
      <span className={`${F.deco} w-12 shrink-0 truncate text-right`}>
        {usage && usage.count > 0 ? t("visitCount", { count: usage.count }) : null}
      </span>

      {onHide ? (
        <div className="absolute right-1 top-1/2 z-10 flex -translate-y-1/2 items-center gap-0.5 rounded-lg bg-white/95 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {onMove && pinned ? (
            <>
              <HomeIconBtn label={t("pinMoveUp")} onClick={() => onMove(module.id, -1)}>
                <ArrowDownWideNarrow className="h-3 w-3 rotate-180" />
              </HomeIconBtn>
              <HomeIconBtn label={t("pinMoveDown")} onClick={() => onMove(module.id, 1)}>
                <ArrowDownWideNarrow className="h-3 w-3" />
              </HomeIconBtn>
            </>
          ) : null}
          <HomeIconBtn
            label={pinned ? t("unpin") : t("pin")}
            active={pinned}
            onClick={() => onTogglePin?.(module.id)}
          >
            {pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
          </HomeIconBtn>
          {/* 移除：让模块**从常用功能里消失**（置顶做不到这件事，见 use-home-prefs 注释）。
              加回入口是标题栏的「添加」——移除后卡片自己就没了，回收路径必须在别处。 */}
          <HomeIconBtn label={t("frequent.hide")} onClick={() => onHide(module.id)}>
            <EyeOff className="h-3 w-3" />
          </HomeIconBtn>
        </div>
      ) : null}
    </div>
  );
}

// ── 常用功能（唯一模块入口） ────────────────────────────────────

const SORTS: { value: VisitSort; labelKey: string }[] = [
  { value: "recent", labelKey: "home.sort.recent" },
  { value: "frequent", labelKey: "home.sort.frequent" },
];

export function FrequentPanel({
  locale,
  prefs,
  visits,
  ready,
  onTogglePin,
  onSort,
  onMove,
  onHide,
  onShow,
}: {
  locale: string;
  prefs: { pinned: HomeModuleId[]; hidden: HomeModuleId[]; sort: VisitSort };
  visits: { path: string; count: number; lastAt: string }[];
  ready: boolean;
  onTogglePin: (id: HomeModuleId) => void;
  onSort: (s: VisitSort) => void;
  onMove: (id: HomeModuleId, toIndex: number) => void;
  /** 移除：从常用功能里拿掉（卡片悬停工具条里的 ⊘） */
  onHide: (id: HomeModuleId) => void;
  /** 加入：把已移除的模块放回常用功能（「添加」区里的 +） */
  onShow: (id: HomeModuleId) => void;
}) {
  const t = useTranslations("home");
  // 🔴 SORTS[].labelKey 是根路径 `home.sort.*`，必须用根翻译器（同上）
  const tRoot = useTranslations();
  const { pinned, rest, usage, hidden } = useSortedModules(prefs, visits, locale);
  /**
   * 「添加」区的展开态。
   *
   * 🔴 默认收起：一个平时不用的管理面板常驻展开，等于把「移除」的成就又还回去 ——
   *    面板上会永远挂着「已移除」分组。收起时用**数量徽标**提示里面还有东西
   *    （`hidden.length`），所以「移除了但找不到在哪加回来」不会发生。
   */
  const [manage, setManage] = useState(false);

  // 把「上移/下移」翻译成目标下标，交给通用 movePin（它内部做边界夹取）
  const move = (id: HomeModuleId, dir: -1 | 1) => {
    const idx = prefs.pinned.indexOf(id);
    onMove(id, idx + dir);
  };

  // 🔴 列数 2/3/4：卡片改成单行横条后，每格**需要的宽度变小了**
  // （图标 24 + 标题 ≤52 + 说明 ~150 + 次数 48 + padding 16 ≈ 290px 上限）。
  // 第 5 轮的 5 列是给「竖排 101px 卡」定的：那时每格只有 200px，
  // 说明文字要 truncate 到 8 个字。横条 + 4 列后每格 ≈290px，
  // 最长的一条说明（「文生图 / 图生图 / 视频生成」13 字）也能完整显示。
  const GRID = "grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-4";

  return (
    <HomeSection
      title={t("frequent.title")}
      // 未挂载完成时**不渲染排序控件的高亮态**：否则首帧会闪一下错误的选中态
      hint={ready ? t("frequent.hint") : undefined}
      action={
        <div className="flex items-center gap-1" data-frequent-sort>
          {SORTS.map((s) => (
            <HomeChip key={s.value} active={ready && prefs.sort === s.value} onClick={() => onSort(s.value)}>
              {tRoot(s.labelKey)}
            </HomeChip>
          ))}
          {/* 「添加」：移除操作的**唯一回收路径**。徽标显示还有几个被移除的功能，
              所以徽标为 0 时不给红点 —— 没有东西可加的时候提示红点就是噪音。 */}
          <HomeChip
            active={ready && manage}
            onClick={() => setManage((v) => !v)}
            title={t("frequent.manageHint")}
            data-frequent-manage
          >
            <span className="flex items-center gap-1">
              <Plus className="h-3 w-3" aria-hidden />
              {t("frequent.manage")}
              {hidden.length > 0 ? (
                <span className="rounded-full bg-[var(--primary)]/15 px-1 text-[10px] leading-4 text-[var(--primary)]">
                  {hidden.length}
                </span>
              ) : null}
            </span>
          </HomeChip>
        </div>
      }
      bodyClassName="flex flex-col gap-1.5"
    >
      {!ready ? (
        // 水合占位：与挂载后同高，避免偏好载入后布局跳动
        <div className={GRID}>
          {/* 数量 = 注册表条数 —— 写死数字会随注册表增删失配（灵感条目 12→8 时
              会水合前后列数不一致），改跟着 HOME_MODULES.length 走。 */}
          {Array.from({ length: HOME_MODULES.length }, (_, i) => (
            <div key={i} className="h-[34px] animate-shimmer rounded-lg" aria-hidden />
          ))}
        </div>
      ) : (
        <>
          {pinned.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <p className={`${F.deco} px-0.5`}>{t("frequent.pinnedGroup")}</p>
              <div className={GRID}>
                {pinned.map((m) => (
                  <ModuleCard
                    key={m.id}
                    module={m}
                    href={resolveModuleHref(m, locale)}
                    usage={usage.get(m.id)}
                    pinned
                    onTogglePin={onTogglePin}
                    onMove={move}
                    onHide={onHide}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {/* 有置顶时才给剩余模块加分组标题：唯一一组时标题是噪音。
              🔴 快捷入口删除后，这里承载**全部 9 个模块**的入口职责，
              不能因为用户没置顶就少渲染任何模块。
              （「移除」是用户显式动作，被移除的模块不算「漏渲染」——
                它们出现在下方「已移除」区，入口没有丢。） */}
          {pinned.length > 0 && rest.length > 0 ? (
            <p className={`${F.deco} px-0.5`}>{t("frequent.allGroup")}</p>
          ) : null}
          <div className={GRID}>
            {rest.map((m) => (
              <ModuleCard
                key={m.id}
                module={m}
                href={resolveModuleHref(m, locale)}
                usage={usage.get(m.id)}
                onTogglePin={onTogglePin}
                onHide={onHide}
              />
            ))}
          </div>

          {/* 全部移除后的空态：不能只留一片空白 —— 那读作「页面坏了」而不是
              「我自己收起来了」。文案必须指向「添加」，否则用户没有下一步。 */}
          {pinned.length === 0 && rest.length === 0 ? (
            <p className={`${F.deco} rounded-lg border border-dashed border-[var(--border-subtle)] px-2 py-3 text-center`}>
              {t("frequent.allHidden")}
            </p>
          ) : null}

          {manage ? (
            <div
              className="flex flex-col gap-1.5 rounded-lg border border-dashed border-[var(--border-subtle)] bg-[var(--surface)]/50 p-2"
              data-frequent-manage-body
            >
              <p className={`${F.deco} px-0.5`}>{t("frequent.manageHint")}</p>
              {hidden.length > 0 ? (
                <>
                  <p className={`${F.deco} px-0.5`}>{t("frequent.hiddenGroup")}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {hidden.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => onShow(m.id)}
                        data-module-restore={m.id}
                        className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] bg-white px-2 py-1 text-xs text-[var(--text-secondary)] transition-colors hover:border-[var(--primary)]/40 hover:text-[var(--primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]"
                      >
                        <ModuleIcon name={m.icon} className="h-3.5 w-3.5" />
                        <span className="font-medium text-[var(--text-primary)]">{tRoot(m.labelKey)}</span>
                        <span className="flex items-center gap-0.5 text-[var(--primary)]">
                          <Plus className="h-3 w-3" aria-hidden />
                          {t("frequent.restore")}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className={`${F.deco} px-0.5`}>{t("frequent.hiddenEmpty")}</p>
              )}
            </div>
          ) : null}
        </>
      )}
    </HomeSection>
  );
}
